import { ValidatorFn, Validators } from '@angular/forms';
import { ValidationPatterns } from './validation-patterns';
import { CustomValidators } from './custom-validators';
import { CreditCardTypeCardBrandName } from 'ngx-unificator/services';
import { PaymentAction } from '../vendor/payment-v3/platform-payment-types';
import { reverseRecord } from './utils';
import { map, Observable } from 'rxjs';
import { CountryList$, CurrencyList$, GenderList$ } from '../services/common-data.service';
import { FormTransformer } from './form-transformer';

export enum PaymentFieldEnum {
  FIRST_NAME = 'FIRST_NAME',
  LAST_NAME = 'LAST_NAME',
  DATE_OF_BIRTH = 'DATE_OF_BIRTH',
  BANKCARD = 'BANKCARD',
  PHONE = 'PHONE',
  EMAIL = 'EMAIL',
  IBAN = 'IBAN',
  POSTAL_CODE = 'POSTAL_CODE',
  BANK_ID = 'BANK_ID',
  AMOUNT = 'AMOUNT',
  ADDRESS = 'ADDRESS',
  CITY = 'CITY',
  COUNTRY = 'COUNTRY',
  CARD_NUMBER = 'CARD_NUMBER',
  CARD_EXPIRATION_DATE = 'CARD_EXPIRATION_DATE',
  CARD_CVV = 'CARD_CVV',
  CARDHOLDER_NAME = 'CARDHOLDER_NAME',
  WALLET_CUSTOMER_ID = 'WALLET_CUSTOMER_ID',
  STATE = 'STATE',
  BANK_CLEARING_SYSTEM_CODE = 'BANK_CLEARING_SYSTEM_CODE',
  BANK_CLEARING_SYSTEM_ID = 'BANK_CLEARING_SYSTEM_ID',
  BAN = 'BAN',
  CRYPTO_CURRENCY = 'CRYPTO_CURRENCY',
  CRYPTO_NETWORK = 'CRYPTO_NETWORK',
  CRYPTO_ADDRESS = 'CRYPTO_ADDRESS',
  CRYPTO_WALLET_ID = 'CRYPTO_WALLET_ID',
}

export type PaymentFieldType = keyof typeof PaymentFieldEnum;

export enum CreditCardFieldEnum {
  CARD_NUMBER = 'CARD_NUMBER',
  CARD_EXPIRATION_DATE = 'CARD_EXPIRATION_DATE',
  CARD_CVV = 'CARD_CVV',
}

export type CreditCardFieldType = keyof typeof CreditCardFieldEnum;

export type CreditCardComponentMode = 'S2S';

export interface ICustomFieldsConfig {
  label?: string;
  tip?: string;
  brand?: string[];
  currencies?: string[];
  action?: PaymentAction;
}

export interface UserFieldDescriptor {
  type: FieldType;
  label: string;
  validator?: ValidatorFn[];
  options?: Observable<[string, string][]>;
}

export interface IFieldsConfig {
  [key: string]: {
    label?: string;
    type?: string;
    hint?: string;
    validator?: any[];
    autocomplete?: string;
    mask?: string;
    prefix?: string;
    maxLength?: number | string;
    label_by_options?: ICustomFieldsConfig[];
    description_by_options?: ICustomFieldsConfig[];
    uppercase?: boolean;
    getValidatorPattern?: (type: CreditCardTypeCardBrandName) => {};
    switchLength?: number;
    placeholder?: string;
    topDescription?: string;
    bottomDescription?: string;
  };
}

export const FieldsDictionaryMapper = {
  FIRST_NAME: 'firstName',
  LAST_NAME: 'lastName',
  BAN: 'ban',
  IBAN: 'iban',
  POSTAL_CODE: 'zipCode',
  BANK_ID: 'bank_account',
  PHONE: 'phoneNumber',
  EMAIL: 'email',
  AMOUNT: 'amount',
  CARD_CVV: 'cvv',
  CARD_NUMBER: 'ccn',
  CARD_EXPIRATION_DATE: 'card_expiry_date',
  CARDHOLDER_NAME: 'cardHolderName',
  DATE_OF_BIRTH: 'dob',
  // BANKCARD
  ADDRESS: 'address',
  CITY: 'city',
  COUNTRY: 'country',
  STATE: 'state',
  WALLET_CUSTOMER_ID: 'wallet_customer_id',
  BANK_CLEARING_SYSTEM_CODE: 'bank_clearing_system_code',
  BANK_CLEARING_SYSTEM_ID: 'bank_clearing_system_id',
  CRYPTO_CURRENCY: 'crypto_currency',
  CRYPTO_NETWORK: 'crypto_network',
  CRYPTO_ADDRESS: 'crypto_address',
  CRYPTO_WALLET_ID: 'crypto_wallet_id',
};

export const ReverseFieldsDictionaryMapper = reverseRecord(FieldsDictionaryMapper);

export enum FieldType {
  TEXT = 'text',
  EMAIL = 'email',
  PASSWORD = 'password',
  SELECT = 'select',
  CHECKBOX = 'checkbox',
  DATE = 'date',
  PHONE = 'phone',
  RADIO = 'radio',
  NUMBER = 'number',
}

export const GlobalFieldsConfig = {
  email: {
    label: 'labl.email',
    type: FieldType.EMAIL,
    validator: [
      FormTransformer.clearDobuleDots,
      FormTransformer.clearDobuleDash,
      Validators.required,
      Validators.maxLength(70),
      Validators.minLength(6),
      Validators.pattern(ValidationPatterns.betterixEmail),
    ],
  },
  firstName: {
    label: 't.first-name',
    type: FieldType.TEXT,
    validator: [
      Validators.required,
      Validators.maxLength(35),
      Validators.minLength(2),
      FormTransformer.clearDobuleSpace,
      Validators.pattern(ValidationPatterns.firsLastName),
    ],
  },
  lastName: {
    label: 't.last-name',
    type: FieldType.TEXT,
    validator: [
      Validators.required,
      Validators.maxLength(35),
      Validators.minLength(2),
      FormTransformer.clearDobuleSpace,
      Validators.pattern(ValidationPatterns.firsLastName),
    ],
  },
  fullName: {
    label: 't.last-name',
    type: FieldType.TEXT,
    validator: [
      Validators.required,
      Validators.maxLength(35),
      Validators.minLength(4),
      FormTransformer.clearDobuleSpace,
      Validators.pattern(ValidationPatterns.firsLastName),
    ],
  },
  middleName: {
    label: 't.last-name',
    type: FieldType.TEXT,
    validator: [
      Validators.required,
      Validators.maxLength(35),
      Validators.minLength(2),
      FormTransformer.clearDobuleSpace,
      Validators.pattern(ValidationPatterns.firsLastName),
    ],
  },
  companyName: {
    label: 't.company-name',
    type: FieldType.TEXT,
    validator: [
      Validators.maxLength(70),
      Validators.minLength(4),
      FormTransformer.clearDobuleSpace,
      Validators.pattern(ValidationPatterns.firsLastName),
    ],
  },
  dob: {
    label: 'DD / MM / YYYY',
    type: FieldType.DATE,
    validator: [Validators.required, Validators.pattern(ValidationPatterns.dateOfBirth), CustomValidators.eighteenYearsOld],
  },
  cardHolderName: {
    // placeholder: 't.card-holder-name',
    label: 't.card-holder-name',
    type: FieldType.TEXT,
    autocomplete: 'cc-name',
    validator: [
      Validators.maxLength(70),
      Validators.minLength(4),
      FormTransformer.clearDobuleSpace,
      Validators.pattern(ValidationPatterns.firsLastName),
    ],
  },
  cardHolde: {
    // placeholder: 't.card-holder-name',
    label: 't.card-holder-name',
    type: FieldType.TEXT,
    autocomplete: 'cc-name',
    validator: [Validators.maxLength(70), Validators.minLength(4), Validators.pattern(ValidationPatterns.firsLastName)],
  },
  cvv: {
    label: 'labl.cvv',
    type: FieldType.TEXT,
    autocomplete: 'cc-csc',
    placeholder: '000',
  },
  phone: {
    label: 'labl.phone',
    type: FieldType.PHONE,
    validator: [
      Validators.required,
      Validators.maxLength(14),
      Validators.minLength(11),
      Validators.pattern(ValidationPatterns.phoneNumber),
    ],
    mask: '+0{15}',
  },
  phoneNumber: {
    label: 'labl.phone',
    type: FieldType.PHONE,
    validator: [Validators.required, Validators.maxLength(16), Validators.minLength(5), Validators.pattern(ValidationPatterns.phoneNumber)],
    mask: '+0{15}',
  },
  tel: {
    label: 'labl.phone',
    type: FieldType.PHONE,
    validator: [
      Validators.required,
      Validators.maxLength(14),
      Validators.minLength(11),
      Validators.pattern(ValidationPatterns.phoneNumber),
    ],
    mask: '+0{15}',
  },
  smsCode: {
    label: 't.code-from-sms',
    type: FieldType.TEXT,
    validator: [Validators.required, Validators.maxLength(6), Validators.minLength(6)],
  },
  encCreditcardNumber: {
    label: 't.cc-number',
    type: 'text',
    autocomplete: 'cc-number',
    validator: [Validators.pattern(ValidationPatterns.creditCard), CustomValidators.luhnValidator],
    placeholder: '4444 5555 6666 1111',
    maxLength: '16',
  },
  ccn: {
    label: 't.cc-number',
    autocomplete: 'cc-number',
    type: 'text',
    validator: [Validators.pattern(ValidationPatterns.creditCard), CustomValidators.luhnValidator],
    placeholder: '4444 5555 6666 1111',
    maxLength: '16',
  },
  expiry_date: {
    label: 't.date',
  },
  expiryDate: {
    label: 't.date',
    placeholder: 't.mmyy',
    validator: [Validators.required, CustomValidators.expiryDateValidator],
    switchLength: 7,
  },
  card_expiry_date: {
    label: 't.date',
    placeholder: 't.mmyy',
    validator: [Validators.required, CustomValidators.expiryDateValidator],
    switchLength: 7,
  },
  expiryMonth: {
    label: 'labl.mm',
    validator: [
      Validators.required,
      Validators.pattern(ValidationPatterns.numbers),
      Validators.maxLength(2),
      Validators.minLength(1),
      Validators.max(12),
      Validators.min(1),
    ],
    placeholder: '00',
  },
  iban: {
    label: 'labl.iban',
    tip: 't.iban-tip',
    uppercase: true,
    validator: [Validators.maxLength(35), Validators.minLength(12)],
  },
  ban: {
    label: 't.ban',
    validator: [Validators.pattern(ValidationPatterns.numbers)],
  },
  wallet_customer_id: {
    label: 't.wallet-customer-id',
    validator: [],
  },
  bank_clearing_system_code: {
    label: 't.bank-clearing-system-code',
    validator: [],
  },
  bank_clearing_system_id: {
    label: 't.bank-clearing-system-id',
    placeholder: '000000',
    validator: [Validators.pattern(ValidationPatterns.numbers)],
    countryConfig: {
      AU: {
        prefix: 'AUBSB',
        mask: '000000',
      },
      NZ: {
        prefix: 'NZNCC',
        mask: '000000',
      },
      CA: {
        prefix: 'CACPA',
        mask: '000000000',
      },
    },
  },
  amount: {
    label: 'labl.amount',
    type: FieldType.NUMBER,
    validator: [CustomValidators.decemicalLimit],
  },
  street1: {
    label: 'labl.street',
  },
  street2: {
    label: 'labl.street',
  },
  month: {
    label: 'labl.mm',
    validator: [
      Validators.required,
      Validators.pattern(ValidationPatterns.numbers),
      Validators.maxLength(2),
      Validators.minLength(1),
      Validators.max(12),
      Validators.min(1),
    ],
  },
  expiryYear: {
    label: 'labl.yy',
    validator: [Validators.required, Validators.pattern(ValidationPatterns.numbers), Validators.maxLength(2), Validators.minLength(1)],
    placeholder: '00',
  },
  year: {
    label: 'yy',
    validator: [Validators.required, Validators.pattern(ValidationPatterns.numbers), Validators.maxLength(2), Validators.minLength(1)],
  },
  password: {
    label: 'labl.password',
    validator: [Validators.required, CustomValidators.passwordValidator],
  },
  username: {
    label: 'labl.username',
  },
  accountNumber: {
    label: 'labl.iban',
    // getValidatorPattern: (): Validators[] => {
    //   return _user.info?.activeWallet?.currency === 'AUD' ? [Validators.pattern(ValidationPatterns.numbers)] :
    //     [Validators.required];
    // },
    uppercase: true,
    mask: 'A{100}0{100}',
    description_by_options: [
      {
        tip: 't.account-number-tip',
        brand: ['inpay'],
        currencies: ['CAD'],
        action: PaymentAction.WITHDRAWAL,
      },
    ],
    label_by_options: [
      {
        label: 'labl.accountNumber',
        brand: ['inpay'],
        currencies: ['CAD'],
        action: PaymentAction.WITHDRAWAL,
      },
    ],
  },
  countryCode: {
    label: 'labl.country-code',
    tip: 't.two-letter-country-code',
    mask: 'SS',
    uppercase: true,
  },
  gender: {
    label: 't.gender',
    type: FieldType.RADIO,
    validator: [Validators.required],
    options: GenderList$,
  },
  currency: {
    label: 't.currency',
    type: FieldType.SELECT,
    options: CurrencyList$.pipe(map(list => list.map(currency => [currency.code, currency.code]))),
    validator: [Validators.required],
  },
  country: {
    label: 't.country',
    type: FieldType.SELECT,
    options: CountryList$.pipe(map(list => list.map(country => [country.short.toUpperCase(), country.name]))),
    validator: [Validators.required],
  },
  address: {
    label: 't.address',
    type: FieldType.TEXT,
    validator: [Validators.required, Validators.maxLength(128), Validators.minLength(4), FormTransformer.clearDobuleSpace],
  },
  city: {
    label: 't.city',
    type: FieldType.TEXT,
    validator: [Validators.required, Validators.maxLength(70), Validators.minLength(4), FormTransformer.clearDobuleSpace],
  },
  state: {
    label: 'labl.state',
    type: FieldType.TEXT,
    validator: [Validators.required, Validators.maxLength(70), Validators.minLength(4), FormTransformer.clearDobuleSpace],
  },
  zipCode: {
    label: 't.zip-code',
    type: FieldType.TEXT,
    validator: [Validators.required, Validators.maxLength(10), Validators.minLength(3), Validators.pattern(ValidationPatterns.zip)],
  },
  crypto_currency: {
    label: 't.crypto-currency',
    type: FieldType.SELECT,
    validator: [Validators.required],
  },
  crypto_address: {
    label: 't.crypto-address',
    type: FieldType.TEXT,
    validator: [
      Validators.required,
    ]
  },
  crypto_wallet_id: {
    label: 't.crypto-wallet-id',
    type: FieldType.TEXT,
    validator: [
      Validators.required,
    ]
  }
};
